import type {
  ApiKey,
  ApiKeyPayload,
  CentralActivity,
  CentralAdmin,
  CentralAdminPayload,
  CentralRole,
  CentralSettingsData,
  CentralSettingsPayload,
  CentralSubscription,
  CheckoutPayload,
  CheckoutResult,
  ChronicDiseaseConsentPayload,
  Contract,
  ContractTemplate,
  DashboardSummary,
  Doctor,
  DoctorAnalytics,
  DoctorFormPayload,
  NotificationPreference,
  ReferenceData,
  Reviewer,
  ReviewerFormPayload,
  ReviewerSummary,
  SessionDetail,
  SessionFormPayload,
  SessionRow,
  SessionSeries,
  SessionStatus,
  SubscriptionPackage,
  SubscriptionRenewalRequest,
} from "@/types/domain";
import type {
  CentralDashboardAnalytics,
  CentralSessionsAnalytics,
} from "@/types/admin";
import type { PermissionGroup } from "@/types/roles";
import { isAxiosError } from "axios";
import type { ListParams, Paginated } from "../api";
import { normalizePaginated, unwrapItem } from "../apiNormalize";
import { buildQuery, del, get, patch, post, put } from "./helpers";

// ——— Central reads ———

export async function fetchDashboard(): Promise<DashboardSummary> {
  const body = await get<unknown>("/central/dashboard");
  return unwrapItem<DashboardSummary>(body);
}

export async function fetchCentralDashboardAnalytics(): Promise<CentralDashboardAnalytics> {
  const body = await get<unknown>("/central/dashboard/analytics");
  return unwrapItem<CentralDashboardAnalytics>(body);
}

export async function fetchCentralSessionsAnalytics(): Promise<CentralSessionsAnalytics> {
  const body = await get<unknown>("/central/sessions/analytics");
  return unwrapItem<CentralSessionsAnalytics>(body);
}

export async function fetchDoctors(params?: ListParams): Promise<Paginated<Doctor>> {
  const body = await get<unknown>(`/central/doctors${buildQuery(params)}`);
  return normalizePaginated<Doctor>(body);
}

export async function fetchDoctor(id: number): Promise<Doctor | undefined> {
  const body = await get<unknown>(`/central/doctors/${id}`);
  return unwrapItem<Doctor>(body);
}

export async function fetchSessions(params?: ListParams): Promise<Paginated<SessionRow>> {
  const body = await get<unknown>(`/central/sessions${buildQuery(params)}`);
  return normalizePaginated<SessionRow>(body);
}

export async function fetchSession(id: number): Promise<SessionDetail | undefined> {
  const body = await get<unknown>(`/central/sessions/${id}`);
  return unwrapItem<SessionDetail>(body);
}

/** Series chain for a session: ancestors root → self plus direct children. */
export async function fetchSessionSeries(id: number): Promise<SessionSeries> {
  const body = await get<unknown>(`/central/sessions/${id}/series`);
  return unwrapItem<SessionSeries>(body);
}

/** Series tip per session for a reviewer: {sessionId: tipSessionId}.
 * Computed server-side so clients never pull full lists just to resolve
 * "continue" targets. */
export async function fetchSeriesTips(reviewerId: number): Promise<Record<number, number>> {
  const body = await get<unknown>(`/central/sessions/series-tips?reviewer_id=${reviewerId}`);
  const item = unwrapItem<{ tips: Record<number, number> }>(body);
  return item.tips ?? {};
}

/** Latest saved contract data for a reviewer + form (template), used to prefill the session form. */
export interface LastContractData {
  contract_id: number | null;
  session_id: number | null;
  form_id: number;
  reviewer_id: number;
  data: Record<string, string>;
}

export async function fetchLastContractData(reviewerId: number, formId: number): Promise<LastContractData> {
  const body = await get<unknown>(
    `/central/sessions/last-contract-data?reviewer_id=${reviewerId}&form_id=${formId}`,
  );
  return unwrapItem<LastContractData>(body);
}

export async function combineSessionsPdf(
  sessionIds: number[],
  options?: { titleAr?: string; titleEn?: string; diseaseAr?: string; diseaseEn?: string; includeContractData?: boolean },
): Promise<{ pdf_url: string; sessions_included: number }> {
  const body = await post<unknown>("/central/sessions/combined-pdf", {
    session_ids: sessionIds,
    title_ar: options?.titleAr,
    title_en: options?.titleEn,
    disease_ar: options?.diseaseAr,
    disease_en: options?.diseaseEn,
    include_contract_data: options?.includeContractData,
  });
  return unwrapItem<{ pdf_url: string; sessions_included: number }>(body);
}

export async function fetchReviewers(params?: ListParams): Promise<Paginated<Reviewer>> {
  const body = await get<unknown>(`/central/reviewers${buildQuery(params)}`);
  return normalizePaginated<Reviewer>(body);
}

export async function fetchReviewer(id: number): Promise<Reviewer | undefined> {
  const body = await get<unknown>(`/central/reviewers/${id}`);
  return unwrapItem<Reviewer>(body);
}

export async function fetchConsents(params?: ListParams): Promise<Paginated<Contract>> {
  const body = await get<unknown>(`/central/consents${buildQuery(params)}`);
  return normalizePaginated<Contract>(body);
}

export async function fetchPendingSessionsCount(): Promise<number> {
  const body = await get<unknown>("/central/sessions/pending-count");
  const item = unwrapItem<{ count: number }>(body);
  return item.count;
}

// ——— Central mutations ———

export async function createDoctor(payload: DoctorFormPayload): Promise<Doctor> {
  const body = await post<unknown>("/central/doctors", payload);
  return unwrapItem<Doctor>(body);
}

export async function updateDoctor(id: number, payload: Partial<DoctorFormPayload>): Promise<Doctor> {
  const body = await put<unknown>(`/central/doctors/${id}`, payload);
  return unwrapItem<Doctor>(body);
}

export async function deleteDoctor(id: number): Promise<void> {
  await del(`/central/doctors/${id}`);
}

export async function restoreDoctor(id: number): Promise<Doctor> {
  const body = await post<unknown>(`/central/doctors/${id}/restore`, {});
  return unwrapItem<Doctor>(body);
}

export async function createReviewer(payload: ReviewerFormPayload): Promise<Reviewer> {
  const body = await post<unknown>("/central/reviewers", payload);
  return unwrapItem<Reviewer>(body);
}

export async function updateReviewer(id: number, payload: ReviewerFormPayload): Promise<Reviewer> {
  const body = await put<unknown>(`/central/reviewers/${id}`, payload);
  return unwrapItem<Reviewer>(body);
}

export async function updateSessionStatus(id: number, status: SessionStatus): Promise<SessionDetail> {
  const body = await patch<unknown>(`/central/sessions/${id}`, { status });
  return unwrapItem<SessionDetail>(body);
}

export async function updateSessionNotes(id: number, notes: string): Promise<void> {
  await patch(`/central/sessions/${id}`, { notes });
}

export async function fetchReferenceData(): Promise<ReferenceData> {
  const body = await get<unknown>("/central/reference");
  return unwrapItem<ReferenceData>(body);
}

export async function fetchConsentTemplates(): Promise<ContractTemplate[]> {
  const body = await get<unknown>("/central/consent-templates");
  const item = unwrapItem<ContractTemplate[] | { data?: ContractTemplate[] }>(body);
  return Array.isArray(item) ? item : (item.data ?? []);
}

export async function fetchDoctorAnalytics(id: number): Promise<DoctorAnalytics> {
  const body = await get<unknown>(`/central/doctors/${id}/analytics`);
  return unwrapItem<DoctorAnalytics>(body);
}

export async function fetchReviewersSummary(): Promise<ReviewerSummary> {
  const body = await get<unknown>("/central/reviewers/summary");
  return unwrapItem<ReviewerSummary>(body);
}

export async function createSession(payload: SessionFormPayload): Promise<SessionDetail> {
  const body = await post<unknown>("/central/sessions", payload);
  return unwrapItem<SessionDetail>(body);
}

export async function updateSession(id: number, payload: Partial<SessionFormPayload>): Promise<SessionDetail> {
  const body = await patch<unknown>(`/central/sessions/${id}`, payload);
  return unwrapItem<SessionDetail>(body);
}

export async function addSessionConsent(sessionId: number, templateId: number): Promise<SessionDetail> {
  const body = await post<unknown>(`/central/sessions/${sessionId}/consents`, {
    template_id: templateId,
  });
  return unwrapItem<SessionDetail>(body);
}

export async function deleteReviewer(id: number): Promise<void> {
  await del(`/central/reviewers/${id}`);
}

export async function restoreReviewer(id: number): Promise<Reviewer> {
  const body = await post<unknown>(`/central/reviewers/${id}/restore`, {});
  return unwrapItem<Reviewer>(body);
}

export async function sendReviewerLink(id: number): Promise<void> {
  await post(`/central/reviewers/${id}/send-link`, {});
}

export async function sendConsentLink(consentId: number): Promise<void> {
  await post(`/central/consents/${consentId}/send-link`, {});
}

// ——— Central subscription self-service ———

export async function fetchCentralSubscription(): Promise<CentralSubscription | null> {
  try {
    const body = await get<unknown>("/central/subscription");
    return unwrapItem<CentralSubscription>(body);
  } catch (err: unknown) {
    if (isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
}

export async function fetchSubscriptionPackages(): Promise<SubscriptionPackage[]> {
  const body = await get<{ data?: SubscriptionPackage[] }>("/central/subscription/packages");
  return body.data ?? [];
}

export async function checkoutSubscription(payload: CheckoutPayload): Promise<CheckoutResult> {
  const body = await post<unknown>("/central/subscription/checkout", payload);
  return unwrapItem<CheckoutResult>(body);
}

export async function fetchSubscriptionRequests(): Promise<SubscriptionRenewalRequest[]> {
  const body = await get<{ data?: SubscriptionRenewalRequest[] }>("/central/subscription/requests");
  return body.data ?? [];
}

export async function createSubscriptionRequest(payload: {
  duration: string;
  notes?: string;
}): Promise<SubscriptionRenewalRequest> {
  const body = await post<unknown>("/central/subscription/requests", payload);
  return unwrapItem<SubscriptionRenewalRequest>(body);
}

export async function extendSubscriptionDoctors(payload: {
  doctors_count: number;
  payment_method: string;
}): Promise<CheckoutResult> {
  const body = await post<unknown>("/central/subscription/extend-doctors", payload);
  return unwrapItem<CheckoutResult>(body);
}

// ——— Central API keys ———

export async function fetchApiKeys(params?: ListParams): Promise<Paginated<ApiKey>> {
  const body = await get<unknown>(`/central/api-keys${buildQuery(params)}`);
  return normalizePaginated<ApiKey>(body);
}

export async function createApiKey(payload: ApiKeyPayload): Promise<ApiKey> {
  const body = await post<unknown>("/central/api-keys", payload);
  return unwrapItem<ApiKey>(body);
}

export async function updateApiKey(id: number, payload: Partial<ApiKeyPayload>): Promise<ApiKey> {
  const body = await put<unknown>(`/central/api-keys/${id}`, payload);
  return unwrapItem<ApiKey>(body);
}

export async function deleteApiKey(id: number): Promise<void> {
  await del(`/central/api-keys/${id}`);
}

export async function revokeApiKey(id: number): Promise<void> {
  await post(`/central/api-keys/${id}/revoke`, {});
}

export async function activateApiKey(id: number): Promise<void> {
  await post(`/central/api-keys/${id}/activate`, {});
}

export async function downloadPostmanCollection(): Promise<void> {
  const { downloadFromApi } = await import("../export");
  await downloadFromApi("/central/api-keys/postman", undefined, "sijilaty-api.postman_collection.json");
}

// ——— Central session cancel ———

export async function cancelSession(id: number): Promise<SessionDetail> {
  const body = await post<unknown>(`/central/sessions/${id}/cancel`, {});
  return unwrapItem<SessionDetail>(body);
}

// ——— Central admins CRUD ———

export async function fetchCentralAdmins(params?: ListParams): Promise<Paginated<CentralAdmin>> {
  const body = await get<unknown>(`/central/admins${buildQuery(params)}`);
  return normalizePaginated<CentralAdmin>(body);
}

export async function createCentralAdmin(payload: CentralAdminPayload): Promise<CentralAdmin> {
  const body = await post<unknown>("/central/admins", payload);
  return unwrapItem<CentralAdmin>(body);
}

export async function updateCentralAdmin(id: number, payload: Partial<CentralAdminPayload>): Promise<CentralAdmin> {
  const body = await put<unknown>(`/central/admins/${id}`, payload);
  return unwrapItem<CentralAdmin>(body);
}

export async function deleteCentralAdmin(id: number): Promise<void> {
  await del(`/central/admins/${id}`);
}

export async function fetchCentralRoles(): Promise<CentralRole[]> {
  const body = await get<{ data?: CentralRole[] }>("/central/roles");
  return body.data ?? [];
}

export async function createCentralRole(payload: { name: string; permissions: string[] }): Promise<CentralRole> {
  const body = await post<unknown>("/central/roles", payload);
  return unwrapItem<CentralRole>(body);
}

export async function updateCentralRole(id: number, payload: { name?: string; permissions?: string[] }): Promise<CentralRole> {
  const body = await put<unknown>(`/central/roles/${id}`, payload);
  return unwrapItem<CentralRole>(body);
}

export async function deleteCentralRole(id: number): Promise<void> {
  await del(`/central/roles/${id}`);
}

export async function fetchCentralPermissionTree(): Promise<PermissionGroup[]> {
  const body = await get<{ data?: PermissionGroup[] }>("/central/permissions/tree");
  return body.data ?? [];
}

// ——— Central settings ———

export async function fetchCentralSettings(): Promise<CentralSettingsData> {
  const body = await get<unknown>("/central/settings");
  return unwrapItem<CentralSettingsData>(body);
}

export async function updateCentralSettings(payload: CentralSettingsPayload): Promise<CentralSettingsData> {
  const body = await put<unknown>("/central/settings", payload);
  return unwrapItem<CentralSettingsData>(body);
}

export async function updateCentralPassword(oldPassword: string, password: string): Promise<void> {
  await put("/central/me/password", { old_password: oldPassword, password });
}

export async function updateCentralMe(payload: { name?: string; email?: string; mobile?: string }): Promise<import("../auth").AuthUser> {
  const body = await put<unknown>("/central/me", payload);
  const data = unwrapItem<{ user: import("../auth").AuthUser }>(body);
  return data.user ?? (data as unknown as import("../auth").AuthUser);
}

// ——— Central activity log ———

export async function fetchCentralActivities(
  params?: ListParams & { entity_type?: string; entity_id?: number },
): Promise<Paginated<CentralActivity>> {
  const body = await get<unknown>(`/central/activities${buildQuery(params)}`);
  return normalizePaginated<CentralActivity>(body);
}

// ——— Impersonation ———

export async function loginAsCentral(id: number): Promise<{ token: string; user: import("../auth").AuthUser }> {
  const body = await post<unknown>(`/admin/centrals/${id}/login-as`, {});
  return unwrapItem<{ token: string; user: import("../auth").AuthUser }>(body);
}

export async function logoutAsCentral(id: number): Promise<void> {
  await post(`/admin/centrals/${id}/logout-as`, {});
}

// ——— Notification preferences ———

export async function fetchNotificationPreferences(): Promise<NotificationPreference[]> {
  const body = await get<{ data?: NotificationPreference[] }>("/central/notification-preferences");
  return body.data ?? [];
}

export async function updateNotificationPreferences(
  preferences: NotificationPreference[],
): Promise<void> {
  await put("/central/notification-preferences", { preferences });
}

// ——— Chronic Disease Consent ———

export async function fetchChronicDiseaseConsents(params?: ListParams): Promise<Paginated<SessionRow>> {
  const body = await get<unknown>(`/central/chronic-disease-consents${buildQuery(params)}`);
  return normalizePaginated<SessionRow>(body);
}

export async function createChronicDiseaseConsent(payload: ChronicDiseaseConsentPayload): Promise<SessionDetail> {
  const body = await post<unknown>("/central/chronic-disease-consents", payload);
  return unwrapItem<SessionDetail>(body);
}

export async function resendChronicDiseaseConsentLink(id: number): Promise<void> {
  await post(`/central/chronic-disease-consents/${id}/send-link`, {});
}
